// ── Spec Resolver ─────────────────────────────────────────────────────────────
// Normalizes and resolves the domain/family of a generation_spec before routing.
//
// FAMILY_MAP is derived from modelRegistry.js — the single source of truth for
// what each model supports. Keyword inference lists live here as a fallback for
// when the LLM emits "unknown" or unrecognized family tags.
//
// To add support for a new domain when a new model is integrated:
//   1. Add the model entry to modelRegistry.js (family_tags + domains)
//   2. Optionally add keywords to the appropriate keyword list below
//   That's it — no changes needed in the router.

import { MODEL_REGISTRY } from '../registry/modelRegistry';

// ── Keyword lists ─────────────────────────────────────────────────────────────

export const CRYSTAL_KEYWORDS = [
  'crystal', 'alloy', 'ceramic', 'inorganic', 'oxide', 'perovskite', 'metal',
  'intermetallic', 'superconductor', 'nitride', 'carbide', 'boride', 'silicide',
  'steel', 'titanium', 'alumina', 'zirconia', 'spinel', 'garnet',
  'refractory', 'hard material', 'MAX phase', 'MXene',
];

export const MOLECULAR_KEYWORDS = [
  'polymer', 'molecule', 'organic', 'drug', 'monomer', 'small molecule',
  'ligand', 'peptide', 'fiber', 'resin', 'epoxy', 'nylon', 'kevlar',
  'polyethylene', 'polyester', 'thermoplastic', 'thermoset', 'elastomer',
  'rubber', 'plastic', 'adhesive', 'coating',
];

export const COMPOSITE_KEYWORDS = [
  'composite', 'hybrid', 'multi-material', 'laminate', 'reinforced',
  'matrix', 'body armor', 'armour', 'armor', 'shield', 'ballistic',
];

// Subset of MOLECULAR_KEYWORDS that map specifically to the 'polymer' domain
const POLYMER_KEYWORDS = new Set([
  'polymer', 'fiber', 'resin', 'epoxy', 'nylon', 'kevlar',
  'polyethylene', 'polyester', 'thermoplastic', 'thermoset', 'elastomer', 'monomer',
]);

// ── Family → domain mapping (derived from modelRegistry) ─────────────────────
// Built automatically from each model's family_tags + domains.
// Composite is a special cross-model tag — it maps to all registered domains
// that appear more than once across models (i.e. genuinely multi-domain requests).

function buildFamilyMap() {
  const map = {};
  // Domain counts — used to determine what "composite" expands to
  const domainCounts = {};
  for (const m of MODEL_REGISTRY) {
    for (const d of m.domains) {
      domainCounts[d] = (domainCounts[d] || 0) + 1;
    }
    for (const tag of m.family_tags) {
      if (!map[tag]) map[tag] = new Set();
      m.domains.forEach(d => map[tag].add(d));
    }
  }
  // Composite → all domains covered by more than one model
  const compositeDomains = Object.entries(domainCounts)
    .filter(([, count]) => count > 1)
    .map(([d]) => d);
  map['composite'] = new Set(compositeDomains.length > 0 ? compositeDomains : Object.keys(domainCounts));

  // Convert Sets → arrays
  return Object.fromEntries(Object.entries(map).map(([k, v]) => [k, Array.from(v)]));
}

export const FAMILY_MAP = buildFamilyMap();
export const KNOWN_FAMILY_TAGS = new Set(Object.keys(FAMILY_MAP));

// ── Main resolver ─────────────────────────────────────────────────────────────

/**
 * Resolve the domain classification of a generation_spec.
 *
 * Runs three stages in order, stopping as soon as domains are found:
 *   1. Family tag mapping   — reads spec.system.family, maps known tags
 *   2. Keyword inference    — scans description + raw prompt for domain keywords
 *   3. Default fallback     — inorganic_crystal if nothing else matched
 *
 * Writes results back to spec.meta:
 *   resolved_domains   — string[]  final domain list
 *   resolution_method  — 'family_tag' | 'keyword_inference' | 'default'
 *   resolution_notes   — human-readable trace for the planned-steps UI
 *
 * Also normalizes spec.system.family to contain only known, resolved tags
 * so the router never sees 'unknown' or unrecognized values.
 *
 * @param {Object} spec - generation_spec object (not mutated — returns a deep clone)
 * @returns {Object} Resolved spec
 */
export function resolveSpec(spec) {
  const s = JSON.parse(JSON.stringify(spec || {}));
  if (!s.meta)   s.meta   = {};
  if (!s.system) s.system = {};
  if (!Array.isArray(s.system.family)) s.system.family = [];

  // ── Fast-path: pre_classified_domains already set ───────────────────────────
  // Written by AppProvider before the Stage 2 decomposer call — either from a
  // category chip selection or the Stage 1 LLM classifier. Takes precedence over
  // family tag mapping and keyword inference so the user's explicit intent is never
  // overridden by LLM-emitted tags or brittle keyword matches.
  if (Array.isArray(s.meta.pre_classified_domains) && s.meta.pre_classified_domains.length > 0) {
    s.meta.resolved_domains  = s.meta.pre_classified_domains;
    s.meta.resolution_method = 'pre_classified';
    s.meta.resolution_notes  = `Pre-classified domains: ${s.meta.pre_classified_domains.join(', ')}`;
    return s;
  }

  const domains      = new Set();
  const resolvedTags = [];
  const notes        = [];
  let method         = 'default';

  // ── Stage 1: map known family tags ─────────────────────────────────────────
  for (const tag of s.system.family) {
    if (KNOWN_FAMILY_TAGS.has(tag)) {
      FAMILY_MAP[tag].forEach(d => domains.add(d));
      resolvedTags.push(tag);
    }
    // Unrecognized/unknown tags are intentionally dropped here.
    // Keyword inference (stage 2) will catch what the LLM meant.
  }

  if (domains.size > 0) {
    method = 'family_tag';
    notes.push(`Family tag(s): ${resolvedTags.join(', ')}`);
  }

  // ── Stage 2: keyword inference ──────────────────────────────────────────────
  if (domains.size === 0) {
    const text = [
      String(s.description || ''),
      String(s.meta?.user_prompt_raw || ''),
    ].join(' ').toLowerCase();

    // Word-boundary match to avoid substring false positives
    // (e.g. "inorganic" must NOT match keyword "organic")
    const wordMatch = (keyword, str) => new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(str);
    const hitComposite = COMPOSITE_KEYWORDS.filter(k => wordMatch(k, text));
    const hitCrystal   = CRYSTAL_KEYWORDS.filter(k => wordMatch(k, text));
    const hitMolecular = MOLECULAR_KEYWORDS.filter(k => wordMatch(k, text));

    if (hitComposite.length > 0) {
      // Composite → both inorganic_crystal and polymer
      domains.add('inorganic_crystal');
      domains.add('polymer');
      resolvedTags.push('composite');
      notes.push(`Composite keywords: "${hitComposite.join('", "')}"`);
    } else {
      if (hitCrystal.length > 0) {
        domains.add('inorganic_crystal');
        resolvedTags.push('inorganic_crystal');
        notes.push(`Crystal keywords: "${hitCrystal.join('", "')}"`);
      }
      if (hitMolecular.length > 0) {
        const isPolymer = hitMolecular.some(k => POLYMER_KEYWORDS.has(k));
        const domain    = isPolymer ? 'polymer' : 'small_molecule';
        domains.add(domain);
        resolvedTags.push(domain);
        notes.push(`Molecular keywords: "${hitMolecular.join('", "')}" → ${domain}`);
      }
    }

    if (domains.size > 0) {
      method = 'keyword_inference';
    }
  }

  // ── Stage 3: default ────────────────────────────────────────────────────────
  if (domains.size === 0) {
    domains.add('inorganic_crystal');
    resolvedTags.push('inorganic_crystal');
    method = 'default';
    notes.push('No family tags or keywords matched — defaulted to inorganic_crystal');
  }

  // ── Write back ──────────────────────────────────────────────────────────────
  s.system.family          = resolvedTags;
  s.meta.resolved_domains  = Array.from(domains);
  s.meta.resolution_method = method;
  s.meta.resolution_notes  = notes.join('; ');

  return s;
}
